import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import type {
  ApiResponse,
  Booking,
  BookingWithPackageUsage,
  CustomerPackage,
  CustomerPackageStatus,
} from '@/models/booking';
import type {
  AuthExistsData,
  AuthLoginRequest,
  AuthResponseData,
  AuthSignupRequest,
  PagedResponse,
  UpdateMeRequest,
  User,
} from '@/models/profile';
import { useUserStore } from '@/store/userStore';

import { STORAGE_KEYS } from './storageKeys';

let isHandlingUnauthorized = false;

class ProfileApiClient {
  private client: AxiosInstance;
  private authClient: AxiosInstance;
  private authClientWithoutStoreId: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.VITE_API_BASE_URL;

    const baseConfig = {
      baseURL,
      timeout: 20000,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    this.client = axios.create(baseConfig);
    this.authClient = axios.create(baseConfig);
    this.authClientWithoutStoreId = axios.create(baseConfig);

    const setHeader = (
      config: InternalAxiosRequestConfig,
      key: string,
      value: string,
    ) => {
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }

      if (config.headers instanceof AxiosHeaders) {
        config.headers.set(key, value);
      } else {
        (config.headers as Record<string, string>)[key] = value;
      }
    };

    const applyCommonHeaders = (
      config: InternalAxiosRequestConfig,
      options?: { includeStoreId?: boolean },
    ): InternalAxiosRequestConfig => {
      const brandId = localStorage.getItem(STORAGE_KEYS.BRAND_ID);
      const storeId = localStorage.getItem(STORAGE_KEYS.STORE_ID);
      const language = localStorage.getItem(STORAGE_KEYS.I18N_LANGUAGE) ?? 'en';
      const includeStoreId = options?.includeStoreId ?? true;

      if (brandId) setHeader(config, 'x-brand-id', brandId);
      if (includeStoreId && storeId) setHeader(config, 'x-store-id', storeId);
      setHeader(config, 'x-language', language);

      return config;
    };

    const applyAuthHeaders = (
      config: InternalAxiosRequestConfig,
      options?: { includeStoreId?: boolean },
    ): InternalAxiosRequestConfig => {
      const next = applyCommonHeaders(config, options);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (token) {
        setHeader(next, 'x-access-token', token);
      }
      return next;
    };

    const attachUnauthorizedHandler = (client: AxiosInstance) => {
      client.interceptors.response.use(
        (response) => response,
        (error: unknown) => {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            if (!isHandlingUnauthorized) {
              isHandlingUnauthorized = true;

              this.clearAuthToken();
              localStorage.removeItem(STORAGE_KEYS.OTP_REF);
              useUserStore.getState().reset();

              if (typeof window !== 'undefined') {
                const path = window.location.pathname;
                const alreadyInOtpFlow =
                  path.endsWith('/profile/phone') ||
                  path.endsWith('/profile/otp');

                if (!alreadyInOtpFlow) {
                  const base = import.meta.env.BASE_URL ?? '/';
                  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
                  window.location.assign(`${normalizedBase}profile/phone`);
                }
              }
            }
          }

          const rejection =
            error instanceof Error ? error : new Error('Request failed');
          return Promise.reject(rejection);
        },
      );
    };

    // Public endpoints: only common headers.
    this.client.interceptors.request.use(applyCommonHeaders);

    // 🔐 Private endpoints: common headers + x-access-token.
    this.authClient.interceptors.request.use((config) => {
      return applyAuthHeaders(config, { includeStoreId: true });
    });

    this.authClientWithoutStoreId.interceptors.request.use((config) => {
      return applyAuthHeaders(config, { includeStoreId: false });
    });

    // Handle expired/invalid session: clear state and restart OTP identify flow.
    attachUnauthorizedHandler(this.authClient);
    attachUnauthorizedHandler(this.authClientWithoutStoreId);
  }

  setAuthToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  clearAuthToken(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  async login(request: AuthLoginRequest): Promise<AuthResponseData> {
    const response = await this.client.post<ApiResponse<AuthResponseData>>(
      '/auth/login',
      request,
    );
    const data = response.data.data;
    this.setAuthToken(data.token);
    return data;
  }

  async signup(request: AuthSignupRequest): Promise<AuthResponseData> {
    const response = await this.client.post<ApiResponse<AuthResponseData>>(
      '/auth/signup',
      request,
    );
    const data = response.data.data;
    this.setAuthToken(data.token);
    return data;
  }

  async checkAuthExists(telPrefix: string, tel: string): Promise<boolean> {
    const response = await this.client.post<ApiResponse<AuthExistsData>>(
      '/auth/exists',
      {
        telPrefix,
        tel,
      },
    );
    return response.data.data.exists;
  }

  async getMe(): Promise<User> {
    const response = await this.authClient.get<ApiResponse<User>>('/users/me');
    return response.data.data;
  }

  async updateMe(request: UpdateMeRequest): Promise<User> {
    const response = await this.authClient.patch<ApiResponse<User>>(
      '/users/me',
      request,
    );
    return response.data.data;
  }

  async getMyBookings(params?: {
    limit?: number;
    page?: number;
  }): Promise<PagedResponse<Booking>> {
    const response = await this.authClientWithoutStoreId.get<
      ApiResponse<PagedResponse<Booking>>
    >('/bookings/me', { params });
    return response.data.data;
  }

  async getMyCustomerPackages(params?: {
    limit?: number;
    page?: number;
    usable?: boolean;
  }): Promise<PagedResponse<CustomerPackage>> {
    const response = await this.authClientWithoutStoreId.get<
      ApiResponse<PagedResponse<CustomerPackage>>
    >('/customer-packages/me', { params });
    return response.data.data;
  }

  async getMyCustomerPackagesStatus(): Promise<CustomerPackageStatus> {
    const response = await this.authClientWithoutStoreId.get<
      ApiResponse<CustomerPackageStatus>
    >('/customer-packages/me/status');
    return response.data.data;
  }

  async getCustomerPackagesById(id: number): Promise<CustomerPackage> {
    const response = await this.authClient.get<ApiResponse<CustomerPackage>>(
      `/customer-packages/${id}`,
    );
    return response.data.data;
  }

  async getCustomerPackageUsage(
    id: number,
    params?: { limit?: number; page?: number },
  ): Promise<PagedResponse<BookingWithPackageUsage>> {
    const response = await this.authClient.get<
      ApiResponse<PagedResponse<BookingWithPackageUsage>>
    >(`/customer-packages/${id}/usage`, { params });
    return response.data.data;
  }
}

export const profileApiClient = new ProfileApiClient();
